import React, { useState, useEffect } from "react";
import { Card, Typography } from "@material-tailwind/react";
import Popup from "reactjs-popup";
import "../../../../styles/popup.css";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import API from "../../../../utils/API";
import CustomTable from "../../../DynamicComponents/CustomTable";
import { useThemeContext } from "../../../../context/ThemeContext";
import StudentDetailsPopUp from "../PopUp/PersonalDetailsPopUp";

const StudentPersonalDetails = ({ setBlurActive }) => {
  const [studentDetails, setStudentDetails] = useState(null);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const user = useSelector((state) => state.auth.user) || {};
  const { roll_no } = user;
  const { darkMode } = useThemeContext();

  const formatDateForInput = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    // Format as YYYY-MM-DD for HTML date input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!roll_no) return;

    const fetchStudentDetails = async () => {
      try {
        const response = await API.get(
          `ece/student/details?roll_no=${roll_no}`
        );
        const student = response?.data?.data?.[0];

        if (student) {
          setStudentDetails([
            {
              roll_no: student.roll_no,
              full_name: student.student_name,
              father_name: student.father_name,
              mother_name: student.mother_name,
              date_of_birth: formatDateForInput(student.date_of_birth),
              gender: student.gender,
              personal_contact: student.personal_contact,
              parent_contact: student.parent_contact,
              personal_email: student.personal_email,
              dtu_email: student.dtu_email,
              original_city: student.original_city,
              original_country: student.original_country,
            },
          ]);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.error || "Error communicating with server"
        );
      }
    };

    fetchStudentDetails();
  }, [roll_no]);

  const openPopup = (student) => {
    setSelectedStudent(student);
    setPopupOpen(true);
    setBlurActive(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setBlurActive(false);
  };

  const handleUpdateStudentDetails = async (updatedStudentDetails) => {
    const {
      full_name,
      father_name,
      mother_name,
      date_of_birth,
      gender,
      personal_contact,
      parent_contact,
      personal_email,
      dtu_email,
      original_city,
      original_country,
    } = updatedStudentDetails;

    const payload = {
      roll_no: roll_no,
      student_name: full_name,
      father_name: father_name,
      mother_name: mother_name,
      date_of_birth: date_of_birth,
      gender: gender,
      personal_contact: personal_contact,
      parent_contact: parent_contact,
      personal_email: personal_email,
      dtu_email: dtu_email,
      original_city: original_city,
      original_country: original_country,
    };

    try {
      const response = await API.put(
        `ece/student/details?roll_no=${roll_no}`,

        payload
      );

      if (response && response.data) {
        toast.success("Student details successfully updated");

        setStudentDetails([
          {
            roll_no: roll_no,
            full_name: full_name,
            father_name: father_name,
            mother_name: mother_name,
            date_of_birth: date_of_birth,
            gender: gender,
            personal_contact: personal_contact,
            parent_contact: parent_contact,
            personal_email: personal_email,
            dtu_email: dtu_email,
            original_city: original_city,
            original_country: original_country,
          },
        ]);

        closePopup();
      } else {
        toast.error("Failed to update student details.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Error communicating with server"
      );
    }
  };

  return (
    <>
      <CustomTable
        title="Personal Details"
        subtitle="(Student's Personal Information)"
        columns={[
          { key: "full_name", label: "Full Name" },
          { key: "father_name", label: "Father's Name" },
          { key: "mother_name", label: "Mother's Name" },
          { key: "date_of_birth", label: "Date of Birth" },
          { key: "gender", label: "Gender" },
          { key: "personal_contact", label: "Personal Contact" },
          { key: "parent_contact", label: "Parent Contact" },
          { key: "personal_email", label: "Personal Email" },
          { key: "dtu_email", label: "DTU Email" },
          { key: "original_city", label: "City" },
          { key: "original_country", label: "Country" },
          { key: "actions", label: "Actions" },
        ]}
        data={studentDetails}
        actions={{
          edit: (student) => {
            openPopup(student);
          },
          delete: () => {
            toast.error("Delete action is not available for personal details.");
          },
        }}
        // Remove onAdd prop since we don't want to add new students
      />

      <Popup
        open={isPopupOpen}
        onClose={closePopup}
        className="mx-auto my-auto p-2"
        closeOnDocumentClick
      >
        <div>
          {selectedStudent && (
            <StudentDetailsPopUp
              full_name={selectedStudent.full_name}
              father_name={selectedStudent.father_name}
              mother_name={selectedStudent.mother_name}
              date_of_birth={selectedStudent.date_of_birth}
              gender={selectedStudent.gender}
              personal_contact={selectedStudent.personal_contact}
              parent_contact={selectedStudent.parent_contact}
              personal_email={selectedStudent.personal_email}
              dtu_email={selectedStudent.dtu_email}
              original_city={selectedStudent.original_city}
              original_country={selectedStudent.original_country}
              closeModal={closePopup}
              handleAddStudentDetails={handleUpdateStudentDetails}
            />
          )}
        </div>
      </Popup>
    </>
  );
};

export default StudentPersonalDetails;
